import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import logger from '../utils/logger';

const EMPTY_NOTE = {
  content: '',
  visible_to_client: false,
  tipo: 'general',
  priority: 'normal',
};

/**
 * useSharedNotes
 *
 * Encapsula estado + handlers del sistema de **notas compartidas** del evento
 * (visibles/no visibles al cliente, con tipo y prioridad):
 *
 *   - fetch automático al cambiar el evento actual
 *   - crear / editar / borrar
 *   - preparar el diálogo (`handleEditNote`)
 *
 * Extraído de `App.js` (feb-2026).
 *
 * @param {Object} params
 * @param {string} params.API                — base URL del backend
 * @param {Object} params.currentPlaylist    — evento actual (usa `.id`)
 */
export function useSharedNotes({ API, currentPlaylist }) {
  const [sharedNotes, setSharedNotes] = useState([]);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState(EMPTY_NOTE);
  const [editingNoteId, setEditingNoteId] = useState(null);

  const fetchSharedNotes = async () => {
    if (!currentPlaylist?.id) return;
    try {
      const response = await axios.get(`${API}/playlists/${currentPlaylist.id}/notes`);
      setSharedNotes(response.data.notes || []);
    } catch (error) {
      // 404 = playlist no existe en backend (ej. caché offline desactualizada).
      // Tratamos como "sin notas" en silencio para no manchar consola/UX.
      if (error?.response?.status === 404) {
        setSharedNotes([]);
        return;
      }
      console.error('Error fetching notes:', error);
    }
  };

  // Auto-fetch cuando cambia el evento
  useEffect(() => {
    if (currentPlaylist?.id) {
      fetchSharedNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlaylist?.id]);

  const handleCreateNote = async () => {
    if (!currentNote.content.trim()) {
      toast.error('El contenido de la nota no puede estar vacío');
      return;
    }
    try {
      await axios.post(`${API}/playlists/${currentPlaylist.id}/notes`, currentNote);
      toast.success('Nota creada exitosamente');
      fetchSharedNotes();
      setIsNotesDialogOpen(false);
      setCurrentNote(EMPTY_NOTE);
    } catch (error) {
      console.error('Error creating note:', error);
      toast.error('Error al crear la nota');
    }
  };

  const handleUpdateNote = async () => {
    if (!currentNote.content.trim()) {
      toast.error('El contenido de la nota no puede estar vacío');
      return;
    }
    try {
      await axios.put(
        `${API}/playlists/${currentPlaylist.id}/notes/${editingNoteId}`,
        currentNote
      );
      toast.success('Nota actualizada exitosamente');
      fetchSharedNotes();
      setIsNotesDialogOpen(false);
      setEditingNoteId(null);
      setCurrentNote(EMPTY_NOTE);
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Error al actualizar la nota');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta nota?')) return;
    try {
      logger.log('[DELETE NOTE] Deleting note:', {
        playlistId: currentPlaylist?.id,
        noteId,
      });
      const url = `${API}/playlists/${currentPlaylist.id}/notes/${noteId}`;
      await axios.delete(url);
      // Update local state immediately
      setSharedNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success('Nota eliminada exitosamente');
      // Refrescar desde servidor para asegurar consistencia
      setTimeout(() => fetchSharedNotes(), 500);
    } catch (error) {
      console.error('[DELETE NOTE] Error deleting note:', error);
      toast.error(
        `Error al eliminar la nota: ${error.response?.data?.detail || error.message}`
      );
    }
  };

  const handleEditNote = (note) => {
    setCurrentNote({
      content: note.content,
      visible_to_client: note.visible_to_client,
      tipo: note.tipo || 'general',
      priority: note.priority || 'normal',
    });
    setEditingNoteId(note.id);
    setIsNotesDialogOpen(true);
  };

  return {
    // state
    sharedNotes,
    setSharedNotes,
    isNotesDialogOpen,
    setIsNotesDialogOpen,
    currentNote,
    setCurrentNote,
    editingNoteId,
    setEditingNoteId,
    // handlers
    fetchSharedNotes,
    handleCreateNote,
    handleUpdateNote,
    handleDeleteNote,
    handleEditNote,
  };
}
